import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import AppView from 'areas/shell/components/AppView';
import ManualLookupDialog from 'areas/cardLookup/components/ManualLookupDialog';
import CardHolderView from 'areas/cards/components/CardHolderView';
import VisitorPassView from 'areas/visitorpass/components/VisitorPassView';
import DualActionButtonPanel from 'components/DualActionButtonPanel';
import SwipeList from 'areas/swipes/components/SwipeList';
import DenySwipeDialog from 'areas/swipes/components/DenySwipeDialog';
import PrimaryDialogButton from 'components/PrimaryDialogButton';
import visitorPassActions from 'areas/visitorpass/actions';
import QRCodeReaderDialog from 'components/QRCodeReaderDialog';
import QRCodeScannerDialog from 'components/QRCodeScannerDialog';
import { uploadSwipe } from 'areas/swipes/actions';
import SwipeEngine from 'areas/swipes/domain/swipeEngine';
import VisitorPassEngine from 'areas/visitorpass/domain/visitorPassEngine';
import MediaQuery from 'react-responsive';
import SwipeInIcon from 'components/Icons/SwipeIn';
import SwipeOutIcon from 'components/Icons/SwipeOut';
import isStringSet from 'utilities/is/isStringSet';
import { getCardCheckerCard, getCurrentCard } from 'areas/cards/selectors';
import { getCurrentVisitorPass } from 'areas/visitorpass/selectors';
import alertActions from 'areas/alert/actions';
import { activeSwipes } from 'areas/swipes/selectors';
import { hideCardholderPage, hideVisitorPassPage } from 'areas/cards/actions';
import m from 'messages';
import {
    showManualLookupDialog,
    hideManualLookupDialog,
    byQRCode,
    byLookup
} from 'areas/cardLookup/actions';
import displayMode from 'areas/swipes/domain/displayMode';
import swipeEvents from 'areas/swipes/domain/swipeEvents';

// import { reactPlugin } from "../../appInsights";
// import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";

const styles = theme => ({
    listPanel: {
        marginBottom: 150
    },
    pageContainer: {
        background: theme.palette.background.paper
    }
});

const SWIPE_OUT_ACTION = "Swipe Out";
const SWIPE_IN_ACTION = "Swipe In";

class SwipesPage extends React.Component {

    constructor(props) {
        super(props);

        this.swipeEngine = new SwipeEngine();
        this.visitorEngine = new VisitorPassEngine();

        this.state = {
            qrCodeDialogOpen: false,
            qrScannerOpen: false,
            denySwipeDialogOpen: false,
            currentSwipeAction: props.currentSwipeAction || '',
            selectedEmployerId: null,
            selectedJobRoleId: null
        };
    }

    /** Handles the selection of the employer from the card holder view */
    handleEmployerChanged = (employerId) => {
        this.setState({ selectedEmployerId: employerId });
    };

    /** Handles the selection of the job role from the card holder view */
    handleJobRoleChanged = (jobRoleId) => {
        this.setState({ selectedJobRoleId: jobRoleId });
    };

    /** Updates the time to work information for the current swipe state */
    handleTimeToWorkChanged = (hours, minutes) => {
        const { card } = this.props;

        card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestHours = hours;
        card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestMinutes = minutes;
    };

    /** Updates the time from work information for the current swipe state */
    handleTimeFromWorkChanged = (hours, minutes) => {
        const { card } = this.props;

        card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestHours = hours;
        card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestMinutes = minutes;
    }

    /** Called to open the deny swipes dialog */
    openDenySwipesDialog = () => {
        this.setState({ denySwipeDialogOpen: true });
    };

    /** Called to close the deny swipes dialog */
    closeDenySwipeDialog = () => {
        this.setState({ denySwipeDialogOpen: false });
    }

    /** Handles a request to close the QR scan dialogs */
    closeQrDialog = () => {
        this.setState({ qrCodeDialogOpen: false, qrScannerOpen: false });
    };

    /** Called from the deny swipes dialog to record a new swipe with the given deny reason */
    onDenySwipe = (denyReason) => {
        const { location, card, pass, cardCheckerCard } = this.props;

        let swipe = null;

        if (card !== null) {
            swipe = this.swipeEngine.manualDeny(card, location, cardCheckerCard, denyReason);
        } else if (pass !== null) {
            swipe = this.swipeEngine.manualDenyVisitorPass(pass, location, cardCheckerCard, denyReason);
        }

        this.setState({ denySwipeDialogOpen: false });
        this.props.hideCardholderPage();
        this.props.uploadSwipe(swipe);
    };

    denySwipeOutSideWorkingHours = () => {
        const denyReason = {
            reasonID: swipeEvents.FATIGUE_OUTSIDE_WORKING_HOURS_ID,
            denyReasonDescription: swipeEvents.FATIGUE_OUTSIDE_WORKING_HOURS_MESSAGE
        };
        const { location, card, cardCheckerCard } = this.props;

        const swipe = this.swipeEngine.systemDeny(card, location, cardCheckerCard, denyReason);

        this.props.hideCardholderPage();
        this.props.uploadSwipe(swipe);
    }

    overrideSwipe = () => {
        this.denySwipeOutSideWorkingHours();
        const { location, card, cardCheckerCard } = this.props;
        let swipe = null;
        if (card) {
            swipe = this.swipeEngine.swipeIn(
                card,
                location,
                cardCheckerCard,
                card.swipeType,
                card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestHours,
                card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestMinutes,
                card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestHours,
                card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestMinutes,
                card.selectedJobRoleID,
                card.selectedEmployerID,
                null);
        }

        this.props.uploadSwipe(swipe);
        this.props.hideCardholderPage()
    }

    /**
     * Called to upload a swipe based on a number of factors:
     * 1. Whether the current action is a swipe in or out
     * 2. Whether the current action is for a physical card, visitor pass or manually looked up person
     */
    confirmSwipe = () => {
        const { location, card, pass, cardCheckerCard } = this.props;
        const { currentSwipeAction } = this.state;

        let swipe = null;
        if (currentSwipeAction === SWIPE_IN_ACTION) {
            if (card) {
                swipe = this.swipeEngine.swipeIn(
                    card,
                    location,
                    cardCheckerCard,
                    card.swipeType,
                    card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestHours,
                    card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestMinutes,
                    card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestHours,
                    card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestMinutes,
                    card.selectedJobRoleID,
                    card.selectedEmployerID,
                    null);
            }
            else if (pass) {
                swipe = this.swipeEngine.swipeVisitorPassIn(pass, location, cardCheckerCard);
            }
        } else {
            if (card !== null) {
                swipe = this.swipeEngine.swipeOut(
                    card,
                    location,
                    cardCheckerCard,
                    card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestHours,
                    card.fatigueInformation.lastSwipeInTravelTimeFromPlaceOfRestMinutes,
                    card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestHours,
                    card.fatigueInformation.lastSwipeInTravelTimeToPlaceOfRestMinutes);
            } else if (pass !== null) {
                swipe = this.swipeEngine.swipeOutVisitorPass(pass, location, cardCheckerCard);
            }
        }

        this.props.uploadSwipe(swipe);
        this.props.hideCardholderPage();
    };

    /** Callback for when a visitor pass is scanned */
    handleVisitorPassScan = (qrCode) => {
        this.closeQrDialog();
        this.props.handleVisitorPassScan(qrCode, this.state.currentSwipeAction);
    };

    /** Callback for when a card is scanned */
    handleScan = (qrCode) => {
        this.closeQrDialog();
        this.props.handleScan(qrCode, this.state.currentSwipeAction, this.props.location.site.siteCode);
    };

    /** Callback for when manual lookup details are entered */
    handleManualLookup = (formData) => {
        this.props.handleManualLookup(formData, this.state.currentSwipeAction, this.props.location.site.siteCode);
    };

    onJobRoleChanged = (jobRoleId) => {
        this.setState({ jobRoleId });
    };

    /** Called when the QR option is clicked */
    qrCodeClicked = (currentContext) => {
        this.setState({
            qrCodeDialogOpen: true,
            currentSwipeAction: currentContext
        });
    };

    qrScannerClicked = (currentContext) => {
        this.setState({
            qrScannerOpen: true,
            currentSwipeAction: currentContext
        });
    };

    /** Called when the forgotten card option is clicked */
    forgottenCardClicked = (currentContext) => {
        this.setState({ currentSwipeAction: currentContext });
        this.props.showManualLookupDialog(currentContext);
    };

    /** Renders the floating action buttons for the page */
    fabButtons = () => {
        return (
            <DualActionButtonPanel
                leftActionText={m.swipeIn._self}
                leftContext={SWIPE_IN_ACTION}
                leftIcon={SwipeInIcon}

                rightActionText={m.swipeOut._self}
                rightContext={SWIPE_OUT_ACTION}
                rightIcon={SwipeOutIcon}

                qrCodeClicked={this.qrCodeClicked}
                qrScannerClicked={this.qrScannerClicked}
                forgottenCardClicked={this.forgottenCardClicked}
            />
        );
    };

    scanError = (message) => {
        this.props.alert(message);
    };

    /** Renders the QR reader dialog */
    qrReaderDialog = () => {
        return (
            <QRCodeReaderDialog
                title={m.scan.scanQrCodeTitle}
                open={this.state.qrCodeDialogOpen}
                handleClose={this.closeQrDialog}
                onCardScanned={this.handleScan}
                onScanError={this.scanError}
                onVisitorPassScanned={this.handleVisitorPassScan} />
        );
    };

    qrScannerDialog = () => {
        return (
            <QRCodeScannerDialog
                title={m.scan.scanQrCodeTitle}
                open={this.state.qrScannerOpen}
                handleClose={this.closeQrDialog}
                onCardScanned={this.handleScan}
                onScanError={this.scanError}
                onVisitorPassScanned={this.handleVisitorPassScan} />
        );
    };

    /** Renders the manual lookup dialog */
    manualLookupDialog = () => {
        return (
            <ManualLookupDialog
                open={!this.props.hasAlert && !!this.props.data.showManualLookupDialog}
                handleClose={this.props.hideManualLookupDialog}
                cardNumberLabel={this.props.config.productNumberName}
                onSubmit={this.handleManualLookup} />
        );
    };

    /** Renders the deny swipes dialog */
    denySwipeDialog = () => {
        const { denyReasons } = this.props;
        return (
            <DenySwipeDialog
                denyReasons={denyReasons}
                open={this.state.denySwipeDialogOpen}
                handleClose={this.closeDenySwipeDialog}
                onDenySwipe={this.onDenySwipe}>
            </DenySwipeDialog>
        );
    };

    /** Renders the visitor pass view */
    swipeInVisitorPassView = (cardConfig, pass, config) => {
        const { showVisitorPassPage } = cardConfig;
        const isOpen = pass && showVisitorPassPage;
        const validateSwipeResult = isOpen ? this.swipeEngine.validateVisitorPassSwipeIn(pass) : null;
        const engineStatus = pass && this.visitorEngine.getVisitorPassStatus(pass);
        const canSwipeIn = validateSwipeResult && validateSwipeResult.canSwipeIn;
        const denyReason = canSwipeIn ? null : { ...validateSwipeResult };

        return (
            <React.Fragment>
                {isOpen &&
                    <VisitorPassView
                        pass={pass.pass}
                        status={engineStatus}
                        config={config}
                        close={this.props.hideVisitorPassPage}
                        disableBackButton={true}
                        title={m.visitorPass._self}
                        open={true}
                        editable={canSwipeIn}
                        fatigueDataEditable={canSwipeIn}
                        denyReason={denyReason}>
                        {canSwipeIn
                            ? [
                                <PrimaryDialogButton key="deny" onClick={this.openDenySwipesDialog}>{m.swipeIn.denySwipe}</PrimaryDialogButton>,
                                <PrimaryDialogButton key="confirm" onClick={this.confirmSwipe}>{m.swipeIn.confirmSwipe}</PrimaryDialogButton>
                            ]
                            : <PrimaryDialogButton onClick={() => this.confirmSwipe()}>{m.general.ok}</PrimaryDialogButton>
                        }
                    </VisitorPassView>
                }
            </React.Fragment>
        );
    };

    /** Renders the card holder view */
    swipeInCardHolderView = (cardConfig, card, config, location) => {
        const { showCardholderPage } = cardConfig;

        const isOpen = card && showCardholderPage;
        const validateSwipeResult = isOpen ? this.swipeEngine.validateSwipeIn(card) : null;

        const canSwipeIn = validateSwipeResult && validateSwipeResult.canSwipeIn;
        const canOverrideSwipeIn = card && validateSwipeResult.canSwipeIn && card.isFatigued;
        if (canOverrideSwipeIn) {
            validateSwipeResult.reasonID = swipeEvents.FATIGUE_OUTSIDE_WORKING_HOURS_ID;
            validateSwipeResult.reasonDescription = swipeEvents.FATIGUE_OUTSIDE_WORKING_HOURS_MESSAGE;
            validateSwipeResult.reasonCaption = m.cards.fatigueWarning;
        }
        const denyReason = canSwipeIn && !canOverrideSwipeIn ? null : { ...validateSwipeResult };
        const canProceed = isOpen && isStringSet(this.state.selectedEmployerId || card.selectedEmployerID);

        return (
            <MediaQuery maxWidth={320}>
                {(isTiny) =>
                    <React.Fragment>
                        {isOpen &&
                            <CardHolderView
                                card={card}
                                config={config}
                                close={this.props.hideCardholderPage}
                                disableBackButton={true}
                                location={location}
                                title={`${card.firstname} ${card.surname}`}
                                open={true}
                                denyReason={denyReason}
                                viewcontext={displayMode.swipeIn}
                                cardHolderProps={{
                                    selectedEmployerID: card.selectedEmployerID || '',
                                    handleEmployerChanged: this.handleEmployerChanged,

                                    selectedJobRoleID: card.selectedJobRoleID || '',
                                    handleJobRoleChanged: this.handleJobRoleChanged,

                                    timeToWorkChanged: this.handleTimeToWorkChanged,
                                    timeFromWorkChanged: this.handleTimeFromWorkChanged,

                                    employerAndJobRoleEditable: canSwipeIn,
                                    showEmployerAndJobRole: true,
                                    fatigueDataEditable: true
                                }}>
                                {canSwipeIn && canOverrideSwipeIn ?
                                    [
                                        <PrimaryDialogButton key="deny" disabled={!canProceed} onClick={this.denySwipeOutSideWorkingHours}> {m.pages.swipes.denySwipeResponsive(isTiny)} </PrimaryDialogButton>,
                                        <PrimaryDialogButton key="override" disabled={!canProceed} onClick={this.overrideSwipe}> {m.pages.swipes.overrideDenySwipeResponsive(isTiny)} </PrimaryDialogButton>
                                    ]
                                    : canSwipeIn ?
                                        [
                                            <PrimaryDialogButton key="deny" disabled={!canProceed} onClick={this.openDenySwipesDialog}> {m.pages.swipes.denySwipeResponsive(isTiny)} </PrimaryDialogButton>,
                                            <PrimaryDialogButton key="confirm" disabled={!canProceed} onClick={this.confirmSwipe}> {m.pages.swipes.confirmSwipeResponsive(isTiny)} </PrimaryDialogButton>
                                        ]
                                        : <PrimaryDialogButton onClick={() => this.confirmSwipe()}> {m.general.ok} </PrimaryDialogButton>
                                }
                            </CardHolderView>
                        }
                    </React.Fragment>
                }
            </MediaQuery>

        );
    };

    /** Renders the visitor pass view for swipe outs */
    swipeOutVisitorPassView = (cardConfig, pass, config) => {
        const { showVisitorPassPage } = cardConfig;
        const isOpen = pass && showVisitorPassPage;

        const engineStatus = pass && this.visitorEngine.getVisitorPassStatus(pass);
        const canSwipeOut = pass && pass.isValid;

        return (
            <React.Fragment>
                {isOpen &&
                    <VisitorPassView
                        pass={pass.pass}
                        status={engineStatus}
                        config={config}
                        close={this.props.hideVisitorPassPage}
                        disableBackButton={true}
                        title={m.visitorPass._self}
                        open={true}
                        editable={canSwipeOut}>
                        <PrimaryDialogButton onClick={this.confirmSwipe}>
                            {canSwipeOut ? m.swipeOut._self : m.general.ok}
                        </PrimaryDialogButton>
                    </VisitorPassView>}
            </React.Fragment>
        );
    };

    /**
     * Renders the card holder view when it is being rendered for swiping out
     */
    swipeOutCardHolderView = (cardConfig, card, config, location) => {
        const { showCardholderPage } = cardConfig;

        const isOpen = card && showCardholderPage;
        const validateSwipeResult = isOpen ? this.swipeEngine.validateSwipeOut(card) : null;
        const canSwipeOut = validateSwipeResult && validateSwipeResult.canSwipeOut;
        const denyReason = canSwipeOut ? null : { ...validateSwipeResult };

        return (
            <React.Fragment>
                {isOpen &&
                    <CardHolderView
                        card={card}
                        config={config}
                        close={this.props.hideCardholderPage}
                        disableBackButton={true}
                        title={`${card.firstname} ${card.surname}`}
                        open={true}
                        location={location}
                        denyReason={denyReason}
                        viewcontext={displayMode.swipeOut}
                        cardHolderProps={{
                            selectedEmployerID: card.selectedEmployerID || '',
                            handleEmployerChanged: this.handleEmployerChanged,

                            selectedJobRoleID: card.selectedJobRoleID || '',
                            handleJobRoleChanged: this.handleJobRoleChanged,

                            timeToWorkChanged: this.handleTimeToWorkChanged,
                            timeFromWorkChanged: this.handleTimeFromWorkChanged,

                            employerAndJobRoleEditable: false,
                            showEmployerAndJobRole: false,
                            fatigueDataEditable: true
                        }}>
                        <PrimaryDialogButton
                            onClick={this.confirmSwipe}>
                            {m.swipeOut._self}
                        </PrimaryDialogButton>
                    </CardHolderView>
                }
            </React.Fragment>
        );
    };

    /**
     * Renders a card holder view based on the current swipe action and selected card
     */
    cardHolderView = () => {
        const { cardConfig, card, config, location } = this.props;
        const currentSwipeAction = this.state.currentSwipeAction;

        const isSwipeIn = currentSwipeAction === SWIPE_IN_ACTION;

        if (isSwipeIn) {
            return this.swipeInCardHolderView(cardConfig, card, config, location);
        } else {
            return this.swipeOutCardHolderView(cardConfig, card, config, location);
        }
    };

    /**
     * Renders the display of the visitor pass if this view is currently active based on current configuration
     */
    visitorPassView = () => {
        const { cardConfig, pass, config, location } = this.props;
        const currentSwipeAction = this.state.currentSwipeAction;
        const isSwipeIn = currentSwipeAction === SWIPE_IN_ACTION;

        if (isSwipeIn) {
            return this.swipeInVisitorPassView(cardConfig, pass, config, location);
        } else {
            return this.swipeOutVisitorPassView(cardConfig, pass, config);
        }
    };

    /** Renders the swipe list for this view */
    swipelist = () => {
        const { swipes, classes, config, location, supervisorData } = this.props;

        return (
            <div className={classes.listPanel}>
                <SwipeList
                    swipes={swipes}
                    config={config}
                    location={location}
                    supervisorData={supervisorData}
                    onSwipeInfoClicked={this.onSwipeInfoClicked} />
            </div>
        );
    };

    /**
     * The main render method for the page
     */
    render() {
        return (
            // <AppInsightsErrorBoundary onError={() => <h1>I believe something went wrong</h1>} appInsights={reactPlugin}>
                <AppView
                    title={this.props.pageTitle}
                    className={this.props.classes.pageContainer}>
                    {this.swipelist()}
                    {this.cardHolderView()}
                    {this.visitorPassView()}
                    {this.fabButtons()}
                    {this.qrReaderDialog()}
                    {this.qrScannerDialog()}
                    {this.denySwipeDialog()}
                    {this.manualLookupDialog()}
                </AppView>
            // </AppInsightsErrorBoundary>
        );
    }
}

function mapStateToProps(state) {
    const { cards, config, data, location, supervisor, swipes, alert } = state;

    const processedCard = getCurrentCard(state);
    const processedCardCheckerCard = getCardCheckerCard(state);
    const processedPass = getCurrentVisitorPass(state);

    return {
        ...supervisor,
        supervisorData: supervisor,
        hasAlert: alert.message && alert.message.length > 0,
        currentSwipeAction: cards.cardholderPageContext || data.manualLookupDialogContext,
        cardConfig: cards,
        cards: cards.cards,
        passes: cards.passes,
        pass: processedPass,
        data,
        config,
        denyReasons: config.denyReasons || [],
        location,
        card: processedCard,
        cardCheckerCard: processedCardCheckerCard,
        swipes: activeSwipes(state),
        uiState: swipes.uiState
    }
}

function mapDispatchToProps(dispatch) {
    return {
        alert: (message, callback) => dispatch(alertActions.error(message, callback)),
        hideManualLookupDialog: () => dispatch(hideManualLookupDialog()),
        hideCardholderPage: () => dispatch(hideCardholderPage()),
        hideVisitorPassPage: () => dispatch(hideVisitorPassPage()),
        uploadSwipe: swipe => dispatch(uploadSwipe(swipe)),
        handleVisitorPassScan: (qrCode, currentSwipeAction) => dispatch(visitorPassActions.byQRCode(qrCode, currentSwipeAction)),
        handleScan: (qrCode, currentSwipeAction, siteCode) => dispatch(byQRCode(qrCode, currentSwipeAction, false, siteCode)),
        handleManualLookup: (formData, currentSwipeAction, siteCode) =>
            dispatch(
                byLookup(formData.surname, formData.initial, (formData.dob ? formData.dob.toISODate() : ''), formData.cardNumber, currentSwipeAction, false, siteCode)),
        showManualLookupDialog: (currentContext) => dispatch(showManualLookupDialog(currentContext))
    }
}

const connectedPage = connect(
    mapStateToProps,
    mapDispatchToProps
)(SwipesPage);

const styledPage = withStyles(
    styles
)(connectedPage);

export default styledPage;